import { AuthResponse, LoginDto } from './../../login/auth-models';

import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, map, Observable } from 'rxjs';

import { ApiService } from './api.service';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<AuthResponse | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  isAuthenticated$ = this.currentUser$.pipe(
    map((user) => {
      const hasUser = !!user;
      const hasToken = !!this.tokenService.getToken();
      const isTokenValid = !this.isTokenExpired();
      return hasUser && hasToken && isTokenValid;
    }),
  );

  private router = inject(Router);
  private apiService = inject(ApiService);
  private tokenService = inject(TokenService);

  constructor() {
    this.initializeAuth();
  }

  get currentUser(): AuthResponse | null {
    return this.currentUserSubject.value;
  }

  login(credentials: LoginDto): Observable<AuthResponse> {
    return this.apiService
      .post<AuthResponse>('LoginUser/login', credentials)
      .pipe(
        map((response) => {
          this.handleAuthenticationSuccess(response);
          this.tokenService.setUserData(response);
          return response;
        }),
      );
  }

  logout(): void {
    this.tokenService.removeToken();
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  private handleAuthenticationSuccess(response: AuthResponse): void {
    this.tokenService.setToken(response.token);
    this.currentUserSubject.next(response);
  }

  private initializeAuth(): void {
    const token = this.tokenService.getToken();
    if (token) {
      // Get user data from storage
      const userData = this.tokenService.getUserData();
      if (userData) {
        this.currentUserSubject.next(userData);
      } else {
        // Token exists but no user data, clear invalid state
        this.logout();
      }
    }
  }

  // Helper method to check token expiration
  isTokenExpired(): boolean {
    const token = this.tokenService.getToken();
    if (!token) return true;

    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const expirationDate = new Date(tokenData.exp * 1000);
      return expirationDate < new Date();
    } catch {
      return true;
    }
  }
}
